import type { OrgFinancingProfile } from '@hero/schema';
import type { ApplyFormValues } from '../components/ApplyForm/types';

/**
 * Map a persisted `Org.financingProfile` onto the wizard's local
 * value shape (strings + yes/no tri-states for controlled inputs).
 */
export function profileToFormValues(
  profile: OrgFinancingProfile | null | undefined,
): Partial<ApplyFormValues> {
  if (!profile) return {};
  return {
    fleetTier: profile.fleetTier,
    annualRevenueTier: profile.annualRevenueTier,
    financingNeeds: profile.financingNeeds ?? [],
    ownsAllTrucks: profile.ownsAllTrucks ? 'yes' : 'no',
    trucksOwnedCount:
      profile.trucksOwnedCount != null
        ? String(profile.trucksOwnedCount)
        : '',
    hasLoans: profile.hasLoans ? 'yes' : 'no',
    monthlyFinancingPayment:
      profile.monthlyFinancingPayment != null
        ? String(profile.monthlyFinancingPayment)
        : '',
    isProfitable:
      profile.isProfitable == null
        ? ''
        : profile.isProfitable
          ? 'yes'
          : 'no',
  };
}

export interface FinancingProfileChecks {
  fleetTier: boolean;
  annualRevenueTier: boolean;
  ownsAllTrucks: boolean;
  trucksOwnedCount: boolean;
  hasLoans: boolean;
  monthlyFinancingPayment: boolean;
}

/** Field-by-field completeness for the financing profile gate. */
export function financingProfileChecks(
  profile: OrgFinancingProfile | null | undefined,
): FinancingProfileChecks {
  if (!profile) {
    return {
      fleetTier: false,
      annualRevenueTier: false,
      ownsAllTrucks: false,
      trucksOwnedCount: false,
      hasLoans: false,
      monthlyFinancingPayment: false,
    };
  }

  const ownsAll = profile.ownsAllTrucks;
  const hasLoans = profile.hasLoans;

  return {
    fleetTier: Boolean(profile.fleetTier),
    annualRevenueTier: Boolean(profile.annualRevenueTier),
    ownsAllTrucks: typeof ownsAll === 'boolean',
    trucksOwnedCount:
      ownsAll === true ||
      (profile.trucksOwnedCount != null && profile.trucksOwnedCount >= 1),
    hasLoans: typeof hasLoans === 'boolean',
    monthlyFinancingPayment:
      hasLoans === false ||
      (profile.monthlyFinancingPayment != null &&
        profile.monthlyFinancingPayment > 0),
  };
}

export function isFinancingProfileComplete(
  profile: OrgFinancingProfile | null | undefined,
): boolean {
  return Object.values(financingProfileChecks(profile)).every(Boolean);
}

/** Local draft validity — used inside the profile drawer before save. */
export function isFinancingProfileDraftValid(v: ApplyFormValues): boolean {
  if (!v.fleetTier || !v.annualRevenueTier) return false;
  if (!v.ownsAllTrucks || !v.hasLoans) return false;
  if (v.ownsAllTrucks === 'no') {
    const trucksOwned = Number(v.trucksOwnedCount);
    if (!v.trucksOwnedCount.trim() || !Number.isFinite(trucksOwned) || trucksOwned < 1) {
      return false;
    }
  }
  if (v.hasLoans === 'yes' && !v.monthlyFinancingPayment) return false;
  return true;
}
